import React, {useEffect, useMemo, useState} from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {format, parseISO} from 'date-fns'
import StorefrontIcon from '@mui/icons-material/Storefront'
import {useSnackbar} from 'notistack'

import PageTitle from '../../components/PageTitle'
import {usePaceV2MerchantList} from './usePaceV2MerchantList'
import {setMerchantEngine, setMerchantShadow} from '../AllocationConfig/decisionConfigService'
import {
  PageWrapper,
  SectionCard,
  SectionHeader,
  SectionHeaderLeft,
  SectionTitle,
  SectionBadge,
  OrdersFilterBar,
  SearchInput,
  StyledTable,
  StyledTh,
  StyledTr,
  StyledTd,
  PaginationRow,
  PaginationInfo,
  PaginationBtns,
  LoadingOverlay,
  EmptyState,
  EmptyText,
} from '../pace_analytics/styled'
import {IconButton} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

const PAGE_SIZE = 15

// Stored values: "V2" = PACE live; null/blank/"V1" = the allocation rules decide (the default).
const engineOf = (b) => (b.allocationEngineVersion || 'V1').toUpperCase()

function fmtDatetime(val) {
  if (!val) return null
  try {
    return format(parseISO(val), 'dd MMM yyyy, HH:mm')
  } catch (_) {
    return val
  }
}

function Dash() {
  return <Typography sx={{fontSize: '0.78rem', color: '#bdbdbd'}}>—</Typography>
}

// Copy for the confirmation step. Engine switches change live allocation for every new
// order the merchant pushes, so they are worded harder than the shadow toggle (which never
// touches the live decision).
function confirmCopy({kind, brand, next}) {
  const who = brand.merchantName || brand.merchantOid
  if (kind === 'ENGINE') {
    return next === 'V2'
      ? {
          title: 'Turn PACE live for this merchant?',
          body: `PACE will rank the couriers for ${who} instead of the allocation rules' own order.`,
          warning: 'This applies to every new order from this merchant, immediately.',
          cta: 'Turn PACE live',
          color: 'warning',
        }
      : {
          title: 'Turn PACE off for this merchant?',
          body: `${who} will go back to booking in the allocation rules' own courier order.`,
          warning: 'This applies to every new order from this merchant, immediately.',
          cta: 'Turn PACE off',
          color: 'warning',
        }
  }
  return next
    ? {
        title: 'Enable shadow-compare?',
        body: `PACE will score every order for ${who} alongside the allocation rules, and both will be recorded.`,
        warning: 'The live allocation is not affected — PACE output is recorded only.',
        cta: 'Enable shadow',
        color: 'primary',
      }
    : {
        title: 'Disable shadow-compare?',
        body: `PACE will stop scoring orders for ${who} in shadow.`,
        warning: 'The live allocation is not affected, but comparison data stops accumulating.',
        cta: 'Disable shadow',
        color: 'primary',
      }
}

function ConfirmChangeDialog({request, onCancel, onConfirm}) {
  if (!request) return null
  const {title, body, warning, cta, color} = confirmCopy(request)
  const {brand} = request
  return (
    <Dialog open onClose={onCancel} maxWidth='xs' fullWidth>
      <DialogTitle sx={{fontSize: '1rem', fontWeight: 700}}>{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{fontSize: '0.85rem', color: '#424242'}}>{body}</Typography>
        <Typography sx={{fontSize: '0.7rem', fontFamily: 'monospace', color: '#9e9e9e', mt: 0.75}}>
          {brand.merchantOid}
        </Typography>
        <Alert severity={color === 'warning' ? 'warning' : 'info'} sx={{mt: 1.5, fontSize: '0.78rem'}}>
          {warning}
        </Alert>
        <Typography sx={{fontSize: '0.7rem', color: '#9e9e9e', mt: 1.5}}>
          The change is recorded in the audit log against your login.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} sx={{textTransform: 'none'}}>Cancel</Button>
        <Button onClick={onConfirm} variant='contained' color={color} sx={{textTransform: 'none'}}>
          {cta}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default function PaceV2MerchantList() {
  const {isLoading, brands, totalBrands, loadBrands, patchBrand} = usePaceV2MerchantList()
  const {enqueueSnackbar} = useSnackbar()
  const [search, setSearch] = useState('')
  const [versionFilter, setVersionFilter] = useState('ALL')  // ALL | V1 | V2
  const [page, setPage] = useState(0)
  const [savingOid, setSavingOid] = useState(null)  // row being written (disables its controls)
  // Pending change awaiting confirmation: {kind: 'ENGINE'|'SHADOW', brand, next}.
  // Nothing is written until the user confirms — these toggles are one click away from
  // flipping a merchant's live allocation engine.
  const [pending, setPending] = useState(null)

  useEffect(() => {
    loadBrands()
  }, [loadBrands])

  const filtered = useMemo(() => {
    let list = brands
    if (versionFilter !== 'ALL') list = list.filter((b) => engineOf(b) === versionFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(b =>
        b.merchantName?.toLowerCase().includes(q) || b.merchantOid?.toLowerCase().includes(q)
      )
    }
    return list
  }, [brands, search, versionFilter])

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE)
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  const handleSearch = (e) => {
    setSearch(e.target.value)
    setPage(0)
  }

  // Toggle handlers only stage the change; writeEngine/writeShadow run on confirm.
  const requestEngine = (brand, next) => {
    if (!next || savingOid) return
    if (next === engineOf(brand)) return  // clicking the already-selected side
    setPending({kind: 'ENGINE', brand, next})
  }

  const requestShadow = (brand, checked) => {
    if (savingOid) return
    if (!!brand.paceV2ShadowEnabled === checked) return
    setPending({kind: 'SHADOW', brand, next: checked})
  }

  const writeEngine = async (oid, next) => {
    if (!next || savingOid) return
    setSavingOid(oid)
    try {
      await setMerchantEngine(oid, next)
      patchBrand(oid, {allocationEngineVersion: next})
      enqueueSnackbar(`Allocation engine set to ${next}`, {variant: 'success'})
    } catch (_) {
      enqueueSnackbar('Failed to update engine', {variant: 'error'})
    } finally {
      setSavingOid(null)
    }
  }

  const writeShadow = async (oid, checked) => {
    if (savingOid) return
    setSavingOid(oid)
    try {
      await setMerchantShadow(oid, checked)
      patchBrand(oid, {paceV2ShadowEnabled: checked})
      enqueueSnackbar(`Shadow-compare ${checked ? 'enabled' : 'disabled'}`, {variant: 'success'})
    } catch (_) {
      enqueueSnackbar('Failed to update shadow-compare', {variant: 'error'})
    } finally {
      setSavingOid(null)
    }
  }

  const onConfirmPending = () => {
    if (!pending) return
    const {kind, brand, next} = pending
    setPending(null)
    if (kind === 'ENGINE') writeEngine(brand.merchantOid, next)
    else writeShadow(brand.merchantOid, next)
  }

  return (
    <PageWrapper>
      <PageTitle>PACE · Merchant List</PageTitle>

      <SectionCard>
        <SectionHeader>
          <SectionHeaderLeft>
            <Box sx={{
              width: 32, height: 32, borderRadius: 2,
              backgroundColor: '#ede7f6', display: 'flex',
              alignItems: 'center', justifyContent: 'center', color: '#5e35b1',
            }}>
              <StorefrontIcon sx={{fontSize: 16}} />
            </Box>
            <Box>
              <SectionTitle>PACE Merchants — Live &amp; Shadow</SectionTitle>
              <Typography sx={{fontSize: '0.68rem', color: '#9e9e9e', mt: 0.25}}>
                Turn PACE live per merchant (otherwise the allocation rules decide), and toggle shadow-compare
              </Typography>
            </Box>
          </SectionHeaderLeft>
          <SectionBadge>{totalBrands} Merchants</SectionBadge>
        </SectionHeader>

        <OrdersFilterBar>
          <SearchInput
            placeholder='Search by merchant name or OID…'
            value={search}
            onChange={handleSearch}
          />
          <ToggleButtonGroup
            size='small' exclusive value={versionFilter}
            onChange={(e, v) => { if (v) { setVersionFilter(v); setPage(0) } }}
            sx={{ml: 1}}
          >
            <ToggleButton value='ALL' sx={{textTransform: 'none', py: 0.25}}>All</ToggleButton>
            <ToggleButton value='V1' sx={{textTransform: 'none', py: 0.25}}>Rules</ToggleButton>
            <ToggleButton value='V2' sx={{textTransform: 'none', py: 0.25}}>PACE live</ToggleButton>
          </ToggleButtonGroup>
        </OrdersFilterBar>

        {isLoading ? (
          <LoadingOverlay><CircularProgress size={28} /></LoadingOverlay>
        ) : paged.length === 0 ? (
          <EmptyState><EmptyText>No merchants found</EmptyText></EmptyState>
        ) : (
          <Box sx={{overflowX: 'auto'}}>
            <StyledTable>
              <thead>
                <tr>
                  <StyledTh>#</StyledTh>
                  <StyledTh>Merchant</StyledTh>
                  <StyledTh>Merchant ID</StyledTh>
                  <StyledTh>Allocation</StyledTh>
                  <StyledTh>Shadow-compare</StyledTh>
                  <StyledTh>Enabled At</StyledTh>
                  <StyledTh>Enabled By</StyledTh>
                </tr>
              </thead>
              <tbody>
                {paged.map((brand, idx) => {
                  const globalIdx = page * PAGE_SIZE + idx + 1
                  const isLast = idx === paged.length - 1
                  const enabledAt = fmtDatetime(brand.paceEnabledAt)
                  const eng = engineOf(brand)
                  const rowSaving = savingOid === brand.merchantOid
                  return (
                    <StyledTr key={brand.merchantOid || idx} isLast={isLast}>
                      <StyledTd>
                        <Typography sx={{fontSize: '0.75rem', color: '#bdbdbd'}}>{globalIdx}</Typography>
                      </StyledTd>
                      <StyledTd bold>
                        {brand.merchantName ? (
                          <Typography sx={{fontSize: '0.82rem', fontWeight: 700, color: '#424242'}}>
                            {brand.merchantName}
                          </Typography>
                        ) : (
                          <Box>
                            <Typography sx={{fontSize: '0.75rem', color: '#bdbdbd', fontStyle: 'italic'}}>
                              Name unavailable
                            </Typography>
                            <Typography sx={{fontSize: '0.65rem', fontFamily: 'monospace', color: '#9e9e9e', mt: 0.25}}>
                              {brand.merchantOid}
                            </Typography>
                          </Box>
                        )}
                      </StyledTd>
                      <StyledTd>
                        <Typography sx={{fontSize: '0.75rem', fontFamily: 'monospace', color: '#757575'}}>
                          {brand.merchantId ?? '—'}
                        </Typography>
                      </StyledTd>
                      <StyledTd>
                        <ToggleButtonGroup
                          size='small' exclusive value={eng}
                          onChange={(e, v) => requestEngine(brand, v)}
                          disabled={rowSaving}
                        >
                          <ToggleButton value='V1' sx={{textTransform: 'none', py: 0, px: 1}}>Rules</ToggleButton>
                          <ToggleButton value='V2' sx={{textTransform: 'none', py: 0, px: 1}}>PACE live</ToggleButton>
                        </ToggleButtonGroup>
                      </StyledTd>
                      <StyledTd>
                        <Tooltip title={eng === 'V2'
                          ? 'Shadow-compare applies to merchants not live on PACE. This merchant is already live.'
                          : 'Score PACE alongside the allocation rules and record both — no effect on the live allocation.'}>
                          <span>
                            <Switch
                              size='small'
                              checked={!!brand.paceV2ShadowEnabled}
                              disabled={eng === 'V2' || rowSaving}
                              onChange={(e) => requestShadow(brand, e.target.checked)}
                            />
                          </span>
                        </Tooltip>
                      </StyledTd>
                      <StyledTd>
                        {enabledAt ? (
                          <Typography sx={{fontSize: '0.78rem', color: '#424242'}}>{enabledAt}</Typography>
                        ) : <Dash />}
                      </StyledTd>
                      <StyledTd>
                        {brand.paceEnabledBy
                          ? <Typography sx={{fontSize: '0.75rem', color: '#757575'}}>{brand.paceEnabledBy}</Typography>
                          : <Dash />
                        }
                      </StyledTd>
                    </StyledTr>
                  )
                })}
              </tbody>
            </StyledTable>
          </Box>
        )}

        {!isLoading && filtered.length > 0 && (
          <PaginationRow>
            <PaginationInfo>
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length} results
            </PaginationInfo>
            <PaginationBtns>
              <IconButton size='small' disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                <ChevronLeftIcon fontSize='small' />
              </IconButton>
              <IconButton size='small' disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>
                <ChevronRightIcon fontSize='small' />
              </IconButton>
            </PaginationBtns>
          </PaginationRow>
        )}
      </SectionCard>

      <ConfirmChangeDialog
        request={pending}
        onCancel={() => setPending(null)}
        onConfirm={onConfirmPending}
      />
    </PageWrapper>
  )
}
